import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const MAX_TITLE = 65;
const MAX_BODY = 240;

const AUDIENCES = [
  { value: 'all', label: 'Everyone' },
  { value: 'sellers', label: 'Sellers' },
  { value: 'buyers', label: 'Buyers' },
];

const audienceLabel = (value) => (AUDIENCES.find(a => a.value === value) || {}).label || value;
const people = (n) => `${n ?? 0} user${n === 1 ? '' : 's'}`;
const formatDate = (d) => (d ? new Date(d).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : '');

/** Send a message to all users (or only sellers or buyers) as a push notification and an in-app notification. */
const BroadcastPage = () => {
  const [audiences, setAudiences] = useState(null);
  const [history, setHistory] = useState([]);
  const [audience, setAudience] = useState('all');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const [a, h] = await Promise.all([api.get('/admin/broadcasts/audiences'), api.get('/admin/broadcasts')]);
      setAudiences(a.data);
      setHistory(h.data);
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || 'Could not load broadcasts' });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // While a broadcast is going out, refresh its progress every few seconds
  useEffect(() => {
    if (!history.some(b => b.status === 'sending')) return undefined;
    const timer = setInterval(() => api.get('/admin/broadcasts').then(r => setHistory(r.data)).catch(() => {}), 4000);
    return () => clearInterval(timer);
  }, [history]);

  const reach = audiences?.[audience];

  const send = async () => {
    setMessage(null);
    if (!title.trim() || !body.trim()) {
      setMessage({ type: 'error', text: 'Add a title and a message.' });
      return;
    }
    const pushNote = audiences?.push_enabled
      ? `${reach?.with_push ?? 0} of them get a push notification on their phone; everyone sees it in the app's notifications.`
      : 'Push notifications are switched off on the server, so it will only appear in the app\'s notifications.';
    if (!window.confirm(`Send "${title.trim()}" to ${audienceLabel(audience).toLowerCase()} (${people(reach?.users)})?\n\n${pushNote}\n\nThis can't be undone.`)) return;
    setSending(true);
    try {
      await api.post('/admin/broadcasts', { title: title.trim(), body: body.trim(), audience, confirmed: true });
      setMessage({ type: 'success', text: 'Broadcast started. Its progress shows below.' });
      setTitle('');
      setBody('');
      load();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || 'The broadcast could not be sent' });
    } finally {
      setSending(false);
    }
  };

  const label = { display: 'block', fontSize: '13px', color: '#555', margin: '14px 0 4px', fontWeight: 600 };
  const input = { width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit' };
  const counter = (n, max) => <span style={{ float: 'right', fontWeight: 400, color: n > max ? '#c0392b' : '#999' }}>{n}/{max}</span>;

  return (
    <div>
      <h1 style={{ marginBottom: '4px' }}>Broadcast message</h1>
      <p style={{ marginTop: 0, color: '#666' }}>
        Send a message to registered users. It arrives as a push notification on phones with notifications on, and in everyone's notifications in the app.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', alignItems: 'start' }}>
        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          {audiences && !audiences.push_enabled && (
            <p style={{ margin: '0 0 12px', padding: '10px', background: '#fff3cd', color: '#856404', borderRadius: '6px', fontSize: '13px' }}>
              Push notifications are switched off on the server (Firebase isn't configured). Messages will only show in the app's notifications.
            </p>
          )}
          <span style={{ ...label, marginTop: 0 }}>Send to</span>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {AUDIENCES.map(a => {
              const r = audiences?.[a.value];
              const selected = audience === a.value;
              return (
                <button key={a.value} type="button" onClick={() => setAudience(a.value)}
                  style={{ width: 'auto', padding: '8px 14px', borderRadius: '20px', cursor: 'pointer', fontSize: '13px',
                    border: `2px solid ${selected ? '#00aacc' : '#ddd'}`, background: selected ? '#e6f7fb' : 'white', color: '#2c3e50' }}>
                  <strong>{a.label}</strong>{r ? ` · ${people(r.users)}, ${r.with_push} with push` : ''}
                </button>
              );
            })}
          </div>

          <label style={label}>Title {counter(title.length, MAX_TITLE)}
            <input value={title} onChange={e => setTitle(e.target.value)} maxLength={MAX_TITLE} placeholder="e.g. New school shops on Eduthrift" style={{ ...input, marginTop: '4px', fontWeight: 400 }} />
          </label>
          <label style={label}>Message {counter(body.length, MAX_BODY)}
            <textarea value={body} onChange={e => setBody(e.target.value)} maxLength={MAX_BODY} rows={4}
              placeholder="What would you like to tell them?" style={{ ...input, marginTop: '4px', resize: 'vertical', fontWeight: 400 }} />
          </label>

          {message && (
            <p style={{ margin: '12px 0 0', color: message.type === 'error' ? '#c0392b' : '#27ae60', fontSize: '14px' }}>{message.text}</p>
          )}

          <button type="button" onClick={send} disabled={sending || !title.trim() || !body.trim()}
            style={{ width: '100%', marginTop: '16px', padding: '12px', border: 'none', borderRadius: '6px', fontSize: '15px', fontWeight: 600, color: 'white',
              cursor: sending ? 'wait' : 'pointer', backgroundColor: !title.trim() || !body.trim() ? '#bdc3c7' : '#00aacc' }}>
            {sending ? 'Sending…' : `Send to ${audienceLabel(audience).toLowerCase()}${reach ? ` (${people(reach.users)})` : ''}`}
          </button>
        </div>

        <div>
          <span style={{ ...label, marginTop: 0 }}>Preview on a phone</span>
          <div style={{ background: '#2c3e50', borderRadius: '16px', padding: '16px' }}>
            <div style={{ background: 'rgba(255,255,255,0.95)', borderRadius: '12px', padding: '10px 12px', display: 'flex', gap: '10px' }}>
              <img src="/favicon.png" alt="" style={{ width: '32px', height: '32px', borderRadius: '8px' }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '12px', color: '#888' }}>EDUTHRIFT · now</div>
                <div style={{ fontWeight: 700, fontSize: '14px', color: '#111', overflowWrap: 'anywhere' }}>{title || 'Your title'}</div>
                <div style={{ fontSize: '13px', color: '#333', overflowWrap: 'anywhere' }}>{body || 'Your message'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <h2 style={{ marginTop: '28px' }}>Sent broadcasts</h2>
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        {history.length === 0 ? (
          <p style={{ padding: '16px', margin: 0, color: '#7f8c8d' }}>No broadcasts yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ textAlign: 'left', background: '#f8f9fa' }}>
                {['Sent', 'Message', 'To', 'Users', 'Pushes', 'Status'].map(h => <th key={h} style={{ padding: '10px 12px' }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {history.map(b => (
                <tr key={b.id} style={{ borderTop: '1px solid #eee', verticalAlign: 'top' }}>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{formatDate(b.created_at)}<div style={{ fontSize: '12px', color: '#999' }}>{b.sent_by}</div></td>
                  <td style={{ padding: '10px 12px' }}><strong>{b.title}</strong><div style={{ color: '#555' }}>{b.body}</div></td>
                  <td style={{ padding: '10px 12px' }}>{audienceLabel(b.audience)}</td>
                  <td style={{ padding: '10px 12px' }}>{b.recipients}</td>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                    {b.pushed} delivered{b.push_failed ? <div style={{ color: '#c0392b', fontSize: '12px' }}>{b.push_failed} not delivered</div> : null}
                  </td>
                  <td style={{ padding: '10px 12px', color: b.status === 'sending' ? '#e67e22' : '#27ae60', fontWeight: 600 }}>
                    {b.status === 'sending' ? 'Sending…' : 'Sent'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p style={{ fontSize: '12px', color: '#7f8c8d' }}>
        "Delivered" means Firebase accepted the push for that phone. Users without push still see the message in the app's notifications.
      </p>
    </div>
  );
};

export default BroadcastPage;
